<template>
  <div class="banner-container">
    <div class="banner-wrapper" :id="wrapperId">
      <ul class="swiper-wrapper">
        <li
          class="swiper-slide"
          v-for="(bannerItem, index) in bannerListData"
          :key="index"
        >
          <BannerImage
            :banner-index="index"
            :bannerData="bannerItem"
            :bannerType="bannerType"
            :isMultBrow="isMultBrow"
          />
        </li>
      </ul>
      <div
        v-if="isMultiBanner"
        class="norecent-pagination"
        slot="pagination"
        :id="paginationId"
      ></div>
    </div>
    <!-- 在swiper-slide上面遮一层透明的小程序标签；不能放到slide里面，因为会被swiper吃掉点击事件 -->
    <LanuchWeapp
      v-if="miniAppInfo.appid || miniAppInfo.username"
      class="weapp"
      :appid="miniAppInfo.appid"
      :username="miniAppInfo.username"
      :path="miniAppInfo.path"
      @weappClick="handleMiniAppClick"
    ></LanuchWeapp>
  </div>
</template>
<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator';
import Swiper from 'swiper';
import BannerImage from '../banner-image.vue';
import { BannerConfig, BannerReportInfo, BannerType } from '../../types';
import { windowHelper } from '@tencent/ppd-base-libs';
import { biClick } from '@tencent/fit-bi-sdk';
import LanuchWeapp from '@tencent/wphk-components-launch-weapp/src/LaunchWeapp.vue';
import { BI_TAGS } from '../../constant';
import 'swiper/dist/css/swiper.min.css';
import { isH5JumpInfo, isMiniAppJumpInfo } from '../../business';
import { formatReportInfo } from '../../utils/report';

@Component({
  components: {
    BannerImage,
    LanuchWeapp,
  },
})
export default class SwiperBanner extends Vue {
  /** banner数据 */
  @Prop({ default: [] }) bannerListData!: BannerConfig[];
  /** banner额外选项 */
  @Prop({ default: () => ({}) }) swiperOptions!: any;
  /** 是否支持重复曝光上报 */
  @Prop({ default: true }) isMultBrow!: Boolean;

  /** 给banner-wrapper赋予唯一Id，避免多个实例相互影响 */
  wrapperId = `banner${Math.floor(Math.random() * 10000)}`;
  /** 给pagination赋予唯一Id，避免多个实例相互影响 */
  paginationId = `pagination${Math.floor(Math.random() * 10000)}`;

  /** 在template中使用 */
  bannerType = BannerType.swiper;

  /** keep上报Adtag */
  bannerTag: BannerReportInfo = '';
  /** 当前bannerListData的下标 */
  curRealIndex = 0;
  /** 小程序信息 */
  get miniAppInfo() {
    const { bannerJumpInfo, bannerTag } = this.bannerListData[this.curRealIndex];
    this.bannerTag = bannerTag;
    let appid = '';
    let username = '';
    let path = '';
    if (isMiniAppJumpInfo(bannerJumpInfo)) {
      const { miniAppId, miniAppUsername, miniAppPath } = bannerJumpInfo;
      appid = miniAppId || '';
      username = miniAppUsername || '';
      path = miniAppPath;
    }
    return {
      appid,
      username,
      path,
    };
  }
  /** 是否有多个banner */
  get isMultiBanner() {
    return this.bannerListData.length > 1;
  }
  /** swiper实例 */
  swiperInstance!: any;
  activated() {
    if (this.swiperInstance) {
      // 销毁后再new，避免离开当前上下文后返回无法滚动
      this.swiperInstance.destroy();
      this.$nextTick(() => {
        this.initSwiper();
      });
    }
  }
  mounted() {
    this.initSwiper();
  }
  initSwiper() {
    // 根据唯一Id生成Swiper实例
    this.swiperInstance = new Swiper(`#${this.wrapperId}`, {
      loop: this.isMultiBanner,
      autoplay: {
        delay: 3000,
        disableOnInteraction: false,
      },
      pagination: {
        el: `#${this.paginationId}`,
      },
      speed: 500,
      ...this.swiperOptions,
      on: {
        ...this.swiperOptions?.on,
        click: this.handleClickBanner,
        slideChange: () => {
          this.curRealIndex = this.swiperInstance.realIndex;
        },
      },
    });
  }
  /**
   * 处理banner点击
   * (不可以直接在img上添加click事件，loop模式下被复制出来的img不会有click事件)
   */
  handleClickBanner(event) {
    const bannerIndex = event?.target?.dataset?.bannerIndex;
    const { bannerJumpInfo, bannerTag } = this.bannerListData[bannerIndex];

    this.clickReport(bannerTag, bannerIndex);
    if (isH5JumpInfo(bannerJumpInfo)) {
      windowHelper.redirect(bannerJumpInfo.jumpUrl, 300);
    } else {
      // 如果没有配置跳转url（肯定也不会配置小程序），往外抛事件
      this.$emit('click', this.bannerListData[bannerIndex]);
    }
  }
  /** 点击小程序 */
  handleMiniAppClick() {
    this.clickReport(this.bannerTag, this.curRealIndex);
  }
  /** 点击上报 */
  clickReport(tag: BannerReportInfo, bannerIndex: number) {
    const reportInfo = formatReportInfo(tag);
    biClick(BI_TAGS.bc1, {
      fbannertype: 'swiper',
      fbannerindex: bannerIndex,
      ...reportInfo,
    });
  }
  unmounted() {
    this.swiperInstance.destroy(false, true);
  }
}
</script>
<style lang="less" scoped>
@import (reference) url(../../less/index.less);
.banner-container {
  width: 686 / @rem;
  height: 165 / @rem;
  overflow: hidden;
  .banner-wrapper {
    overflow: hidden;
    position: relative;
    --swiper-theme-color: #fff;
    .swiper-wrapper {
      display: flex;
      transform: translate3d(0, 0, 0);
    }
    .swiper-slide {
      transform: translate3d(0, 0, 0);
      /deep/ .banner-image {
        width: 686 / @rem;
        overflow: hidden;
        pointer-events: none;
      }
    }
  }
  .weapp {
    z-index: 2;
  }
}
</style>

<style lang="less">
@rem: 200rem;
.norecent-pagination {
  bottom: 10 / @rem!important;
  position: absolute;
  text-align: center;
  transition: 300ms opacity;
  transform: translate3d(0, 0, 0);
  z-index: 10;
  line-height: 10 / @rem;
  & .swiper-pagination-bullet-active {
    background: #07c160;
  }
  & .swiper-pagination-bullet {
    width: 10 / @rem;
    height: 10 / @rem;
    border-radius: 50%;
    margin: 0 6 / @rem !important;
  }
}
</style>
